/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useRef } from 'preact/hooks';
import { ComponentChildren } from 'preact';
import { Collapsible } from '../UNSAFE_Collapsible';

import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { equals } from 'ramda';
import { AccordionItemStylingContext } from '../PRIVATE_AccordionItemStylingContext';


export type TransitionEnd<K> = {
 value: {
    from?: K;
    to: K | null;
  }
};


/**
 * Props for the  AccordionItemSingle Component
 */
export type Props<K> = {
  /**
   * The key associated with the  AccordionItemSingle.
   */
  itemKey: K;
  /**
   * The expanded key.
   */
  expandedKey?: K | null;
  /**
   * aria-label for this  AccordionItemSingle
   */
  'aria-label'?: string;
  /**
   * Callback function to invoke when the expanded keys has changed when
   * the item is toggled.
   */
  onChange?: (detail: { value: K | null; target: EventTarget | null;}) => void;
  /**
   * One or more ids (separated by spaces) of elements that label the selected item. This will be used for the aria-labelledby of the DOM element.
   */
  'aria-labelledby'?: string;

  /**
   * AccordionItem's header. If not specified, the header contains only an open/close icon. Note that the header text is required for JET collapsible for accessibility purposes.
   */
  header?: ComponentChildren;

  /**
   * The content node to be collapsed
   */
  children?: ComponentChildren;

  /**
   * Disables the collapsible if set to true
   */
  isDisabled?: boolean;

  /**
   * Changes chevron icon placement at the end of the collapsible header
   */
  iconPosition?: 'start' | 'end';

   /**
   * Property that triggers a callback after toggle animation is done
   */
  onTransitionEnd?: (details: TransitionEnd<K>) => void;
};

export type ToggleDetail =  {
  value: boolean;
  target: EventTarget | null;
};


/**
 * The  AccordionItemSingle component renders checkboxes in collections to support selection.
 */
export function  AccordionItemSingle<K>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  children,
  isDisabled,
  header,
  iconPosition,
  itemKey,
  expandedKey,
  onChange,
  onTransitionEnd,
  testId
}: Props<K> & TestIdProps) {
  // Track previous expandedKey during render phase
  const seenExpandedKeyRef = useRef<K | null>();
  const prevExpandedKeyRef = useRef<K | null>();

  // capture during render before DOM events fire
  if (seenExpandedKeyRef.current !== expandedKey) {
    prevExpandedKeyRef.current = seenExpandedKeyRef.current;
    seenExpandedKeyRef.current = expandedKey;
  }

  const onToggleHandler = useCallback(
    (detail: ToggleDetail) => {
      handleToggle(detail, itemKey, expandedKey, onChange);
    },
    [itemKey, expandedKey, onChange]
  );

  const toggleHandler = useCallback(
    (detail: ToggleDetail) => {
      onToggleHandler(detail);
    },
    [onToggleHandler]
  );

    const handleOnTransitionEnd = useCallback(
      () => {
        onTransitionEnd?.(
          {
          value: { from: prevExpandedKeyRef.current ?? undefined, to: expandedKey || null },
        });
      },
      [onTransitionEnd, expandedKey]
    )

  const isExpanded = equals(expandedKey, itemKey);

  const testIdProps = useTestId(testId);

  return (
    <div
      {...testIdProps}>
      <AccordionItemStylingContext.Provider value={{stylingVariant: 'accordion'}}>  
        <Collapsible
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          isDisabled={isDisabled}
          isExpanded={isExpanded}
          header={header}
          iconPosition={iconPosition}
          variant='basic'
          onToggle={toggleHandler}
          onTransitionEnd={handleOnTransitionEnd}>
          {children}
        </Collapsible>
      </AccordionItemStylingContext.Provider>  
    </div>
  );
}

const handleToggle = <K,>(
  detail: ToggleDetail,
  itemKey: Props<K>['itemKey'],
  expandedKey: Props<K>['expandedKey'],
  onChange?: Props<K>['onChange']
) => {
  
  if (onChange) {
    const newKey = equals(itemKey, expandedKey) == false ? itemKey : null;
    onChange({ value: newKey, target: detail.target });

    // we don't want container component like ListView to process the click event,
    // and we also stop the event propagation in keyup due to issue in JET-60156
    //detail.stopPropagation();
  }
};
